import { state, labelOf } from '@/store/state';
import { setWall, isOnWall, forcePlay, gridApplyDrop } from './grid';
import { esc } from '@/lib/dom';
import * as bus from '@/lib/bus';

export type DragOrigin =
  | { kind: 'panel'; cam: string }
  | { kind: 'tile'; cam: string; fromSlot: number };

interface Session {
  origin: DragOrigin;
  sourceEl: HTMLElement;
  pointerId: number;
  startX: number;
  startY: number;
  moved: boolean;
  avatar: HTMLElement | null;
  lastTarget: HTMLElement | null;
}

const THRESHOLD = 6;
const FEEDBACK = ['drop-ok', 'drop-swap', 'drop-replace', 'insert-left', 'insert-right'];
let session: Session | null = null;

export function attachDragSource(el: HTMLElement, makeOrigin: () => DragOrigin): void {
  el.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button')) return; // let buttons act
    session = {
      origin: makeOrigin(),
      sourceEl: el,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      avatar: null,
      lastTarget: null,
    };
    try { el.setPointerCapture(e.pointerId); } catch { /* synthetic */ }
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
  });
}

function makeAvatar(cam: string): HTMLElement {
  const a = document.createElement('div');
  a.className = 'drag-avatar';
  const label = labelOf(state.cameras[cam] ?? { id: cam, site: null, idx: null, url: '', status: 'unknown', lastSeen: null });
  a.innerHTML = `<span class="chip"></span><span class="da-name">${esc(label)}</span><span class="da-id mono">${esc(cam)}</span>`;
  document.body.appendChild(a);
  return a;
}

function clearFeedback(el: HTMLElement | null): void {
  if (el) el.classList.remove(...FEEDBACK);
}

function onMove(e: PointerEvent): void {
  if (!session || e.pointerId !== session.pointerId) return;
  const dx = e.clientX - session.startX;
  const dy = e.clientY - session.startY;
  if (!session.moved) {
    if (Math.hypot(dx, dy) < THRESHOLD) return;
    session.moved = true;
    session.avatar = makeAvatar(session.origin.cam);
    document.body.classList.add('dragging');
    bus.emit('scan:stop', undefined);
  }
  session.avatar!.style.transform = `translate(${e.clientX + 12}px, ${e.clientY + 12}px)`;

  const under = document.elementFromPoint(e.clientX, e.clientY);
  const slot = under?.closest<HTMLElement>('.slot');
  clearFeedback(session.lastTarget);
  session.lastTarget = slot ?? null;
  if (!slot) return;

  if (slot.classList.contains('slot--append') || slot.classList.contains('slot--empty')) {
    slot.classList.add('drop-ok');
  } else if (state.layout === 'fit') {
    const r = slot.getBoundingClientRect();
    slot.classList.add(e.clientX - r.left < r.width / 2 ? 'insert-left' : 'insert-right');
  } else {
    slot.classList.add(session.origin.kind === 'tile' ? 'drop-swap' : 'drop-replace');
  }
}

function onUp(e: PointerEvent): void {
  if (!session || e.pointerId !== session.pointerId) return;
  const s = session;
  const el = s.sourceEl;
  try { el.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  el.removeEventListener('pointermove', onMove);
  el.removeEventListener('pointerup', onUp);
  el.removeEventListener('pointercancel', onUp);
  session = null;
  document.body.classList.remove('dragging');
  s.avatar?.remove();

  if (!s.moved) {
    if (s.origin.kind === 'panel') setWall(s.origin.cam, !isOnWall(s.origin.cam));
    else if (s.sourceEl.dataset['s'] === 'idle') forcePlay(s.origin.cam);
    else bus.emit('spotlight', s.origin.cam);
    clearFeedback(s.lastTarget);
    return;
  }

  const target = s.lastTarget;
  if (target && e.type !== 'pointercancel') gridApplyDrop(s.origin, target);
  clearFeedback(target);
}
